import { FleetAreaCode, PermissionCode, ProductCode } from '@gb-ticket/shared-types';
import { type RouteObject } from 'react-router';

import { RequireAuthLayout, RequireProductLayout } from '@/platform/auth/require-permission';
import { PlatformShellLayout } from '@/platform/layouts/platform-shell';
import '@/products/fleet/product-nav';
import { FleetAdminGate } from '@/products/fleet/features/fleet-admin-gate';
import { FleetAreaGate } from '@/products/fleet/features/fleet-area-gate';
import { FLEET_ROOT } from '@/products/fleet/fleet-paths';
import { FleetLayout } from '@/products/fleet/layouts/fleet-layout';
import { FleetBookPage } from '@/products/fleet/pages/fleet-book-page';
import { FleetDriverDetailPage } from '@/products/fleet/pages/fleet-driver-detail-page';
import { FleetDriversPage } from '@/products/fleet/pages/fleet-drivers-page';
import { FleetEnergyPage } from '@/products/fleet/pages/fleet-energy-page';
import { FleetHomePage } from '@/products/fleet/pages/fleet-home-page';
import { FleetOdometerPage } from '@/products/fleet/pages/fleet-odometer-page';
import { FleetPolicyPage } from '@/products/fleet/pages/fleet-policy-page';
import { FleetReportPage } from '@/products/fleet/pages/fleet-report-page';
import { FleetReportsPage } from '@/products/fleet/pages/fleet-reports-page';
import { FleetTasksPage } from '@/products/fleet/pages/fleet-tasks-page';
import { FleetVehicleDetailPage } from '@/products/fleet/pages/fleet-vehicle-detail-page';
import { FleetVehicleNewPage } from '@/products/fleet/pages/fleet-vehicle-new-page';
import { FleetVehiclesPage } from '@/products/fleet/pages/fleet-vehicles-page';
import { FleetBetriebKartePage } from '@/products/fleet/pages/fleet-betrieb-karte-page';
import { FleetBetriebLadenPage } from '@/products/fleet/pages/fleet-betrieb-laden-page';
import { FleetBetriebKostenPage } from '@/products/fleet/pages/fleet-betrieb-kosten-page';
import { FleetBetriebTankkartenPage } from '@/products/fleet/pages/fleet-betrieb-tankkarten-page';
import { FleetTripsPage } from '@/products/fleet/pages/fleet-trips-page';
import {
  FleetCostsGate,
  FleetOperationsGate,
  FleetOperationsIndex,
} from '@/products/fleet/features/fleet-operations-gate';
import { FleetOperationsLayout } from '@/products/fleet/layouts/fleet-operations-layout';

/** Fleet product (`/fleet`) inside the Mitarbeiterportal chrome. */
export const fleetRoute: RouteObject = {
  Component: RequireAuthLayout,
  children: [
    {
      Component: PlatformShellLayout,
      children: [
        {
          path: FLEET_ROOT,
          element: <RequireProductLayout product={ProductCode.FLEET} />,
          children: [
            {
              Component: FleetLayout,
              children: [
                { index: true, Component: FleetHomePage },
                {
                  element: <FleetAdminGate permission={PermissionCode.FLEET_POOL_BOOK} />,
                  children: [
                    {
                      element: <FleetAreaGate anyOf={[FleetAreaCode.POOL]} />,
                      children: [{ path: 'buchen', Component: FleetBookPage }],
                    },
                  ],
                },
                {
                  element: <FleetAdminGate permission={PermissionCode.FLEET_MILEAGE_WRITE} />,
                  children: [
                    {
                      element: <FleetAreaGate anyOf={[FleetAreaCode.MILEAGE]} />,
                      children: [{ path: 'kilometer', Component: FleetOdometerPage }],
                    },
                  ],
                },
                {
                  element: <FleetAdminGate permission={PermissionCode.FLEET_DAMAGE_CREATE} />,
                  children: [
                    {
                      element: <FleetAreaGate anyOf={[FleetAreaCode.DAMAGE]} />,
                      children: [{ path: 'melden', Component: FleetReportPage }],
                    },
                  ],
                },
                {
                  element: <FleetAdminGate permission={PermissionCode.FLEET_TRIPS_READ_OWN} />,
                  children: [
                    {
                      element: <FleetAreaGate anyOf={[FleetAreaCode.TRIPS]} />,
                      children: [{ path: 'fahrten', Component: FleetTripsPage }],
                    },
                  ],
                },
                {
                  element: (
                    <FleetAreaGate
                      anyOf={[FleetAreaCode.TELEMATICS]}
                      permission={PermissionCode.FLEET_ENERGY_MANAGE}
                    />
                  ),
                  children: [{ path: 'e-management', Component: FleetEnergyPage }],
                },
                {
                  element: <FleetOperationsGate />,
                  children: [
                    {
                      path: 'betrieb',
                      children: [
                        { index: true, Component: FleetOperationsIndex },
                        {
                          Component: FleetOperationsLayout,
                          children: [
                            {
                              element: (
                                <FleetAreaGate
                                  anyOf={[FleetAreaCode.TELEMATICS]}
                                  permission={PermissionCode.FLEET_TELEMATICS_READ}
                                />
                              ),
                              children: [
                                { path: 'karte', Component: FleetBetriebKartePage },
                                { path: 'laden', Component: FleetBetriebLadenPage },
                              ],
                            },
                            {
                              element: (
                                <FleetAreaGate
                                  anyOf={[FleetAreaCode.FUEL]}
                                  permission={PermissionCode.FLEET_FUEL_READ}
                                />
                              ),
                              children: [
                                { path: 'tankkarten', Component: FleetBetriebTankkartenPage },
                              ],
                            },
                            {
                              element: <FleetCostsGate />,
                              children: [{ path: 'kosten', Component: FleetBetriebKostenPage }],
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
                {
                  element: <FleetAdminGate permission={PermissionCode.FLEET_VEHICLES_MANAGE} />,
                  children: [
                    { path: 'fahrzeuge', Component: FleetVehiclesPage },
                    { path: 'fahrzeuge/neu', Component: FleetVehicleNewPage },
                    { path: 'aufgaben', Component: FleetTasksPage },
                    {
                      element: <FleetAreaGate anyOf={[FleetAreaCode.DAMAGE]} />,
                      children: [{ path: 'meldungen', Component: FleetReportsPage }],
                    },
                  ],
                },
                { path: 'fahrzeuge/:id', Component: FleetVehicleDetailPage },
                {
                  element: <FleetAdminGate permission={PermissionCode.FLEET_COMPLIANCE_MANAGE} />,
                  children: [
                    {
                      element: (
                        <FleetAreaGate
                          anyOf={[FleetAreaCode.COMPLIANCE, FleetAreaCode.INSTRUCTION]}
                        />
                      ),
                      children: [
                        { path: 'fahrer', Component: FleetDriversPage },
                        { path: 'fahrer/:userId', Component: FleetDriverDetailPage },
                      ],
                    },
                  ],
                },
                {
                  path: 'verwaltung',
                  element: <FleetAdminGate permission={PermissionCode.FLEET_POLICY_MANAGE} />,
                  children: [{ index: true, Component: FleetPolicyPage }],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};
